import React, { useEffect, useState } from 'react';
import {
    BrowserRouter,
    HashRouter,
    Routes,
    Route,
    Navigate,
    useLocation
} from 'react-router-dom';
import { setupApiClient } from './utils/apiConfig';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import FinancialArticlesPage from './components/FinancialArticlesPage';
import PriceTypesPage from './components/PriceTypesPage';
import CounterpartiesPage from './components/CounterpartiesPage';
import NomenclaturePage from './components/NomenclaturePage';
import TaskNewPage from './components/TaskNewPage';
import TaskPage from './components/TaskPage';
import TaskManagerPage from './components/TaskManagerPage';
import TaskManagerFieldsPage from './components/TaskManagerFieldsPage';
import TaskManagerDictionariesPage from './components/TaskManagerDictionariesPage';
import TaskManagerFieldPermissionsPage from './components/TaskManagerFieldPermissionsPage';
import TaskManagerAccessConstructorPage from './components/TaskManagerAccessConstructorPage';
import TaskManagerFunnelsPage from './components/TaskManagerFunnelsPage';
import TaskManagerProjectsPage from './components/TaskManagerProjectsPage';
import TaskManagerOrganizationsPage from './components/TaskManagerOrganizationsPage';
import TaskManagerSitesPage from './components/TaskManagerSitesPage';
import TaskManagerSettingsPage from './components/TaskManagerSettingsPage';
import TaskManagerWorkTypeStagesPage from './components/TaskManagerWorkTypeStagesPage';
import TaskManagerStatusesPage from './components/TaskManagerStatusesPage';
import ScenariosPage from './components/ScenariosPage';
import IncomercePage from './components/IncomercePage';
import InvoicesPage from './components/InvoicesPage';
import UsersPage from './components/UsersPage';
import OrgStructurePage from './components/OrgStructurePage';
import UserTasksPage from './components/UserTasksPage';
import VariablesInfoPage from './components/VariablesInfoPage';
import RegistryCreatePage from './components/RegistryCreatePage';
import DailyReportsPage from './components/DailyReportsPage';
import DocumentCreatePage from './components/DocumentCreatePage';
import DocWorkflowKanbanPage from './components/DocWorkflowKanbanPage';
import InvoiceApprovalPage from './components/InvoiceApprovalPage';
import EmployeeChatPage from './components/EmployeeChatPage';
import EmployeeChatWidget from './components/EmployeeChatWidget';
import RouteCalculatorPage from './components/RouteCalculatorPage';
import InstallerTimesheetPage from './components/InstallerTimesheetPage';
import InstallerWorkPage from './components/InstallerWorkPage';
import InstallerTmcPage from './components/InstallerTmcPage';
import InstallerAppUpdateGate from './components/InstallerAppUpdateGate';
import BitrixAnalyticsPage from './components/BitrixAnalyticsPage';
import BitrixTasksPage from './components/BitrixTasksPage';
import BitrixTaskDetailPage from './components/BitrixTaskDetailPage';
import CalendarPage from './components/CalendarPage';
import HrDealsKanbanPage from './components/HrDealsKanbanPage';
import HrStaffRegistryPage from './components/HrStaffRegistryPage';
import AvitoOAuthCallbackPage from './components/AvitoOAuthCallbackPage';
import CustomerCabinetPage
    from './components/CustomerCabinetPage';
import UserActivityLogsPage
    from './components/UserActivityLogsPage';
import PrivilegesPage from './components/PrivilegesPage';
import MaxMessengerPage from './components/MaxMessengerPage';
import KeywordWatchPage from './components/KeywordWatchPage';
import TranscriptionPage from './components/TranscriptionPage';
import GoskeySignPage from './components/GoskeySignPage';
import TmcWarehousePage from './components/TmcWarehousePage';
import CustomerOrderComparePage from './components/CustomerOrderComparePage';
import BpClosingPage from './components/BpClosingPage';
import SubcontractsPage from './components/SubcontractsPage';
import {
    KedoLayout,
    KedoLandingPage,
    KedoLoginPage,
    KedoRegisterPage,
    KedoAuthGuard,
    KedoDashboardPage,
    KedoDocumentsPage,
    KedoDocumentPage,
    KedoSettingsPage,
    KedoCounterpartiesPage,
    KedoMchdPage,
    KedoAuditPage
} from './components/kedo';

import {
    setupAxiosAuth,
    startSessionWatchers,
    IDLE_TIMEOUT_MS,
    getLastActivityAt,
    clearSessionStorage,
    isInstallerSession
} from './utils/authSession';
import {
    hydrateInstallerSession,
    resolvePortalPath
} from './utils/installerSession';
import { isInstallerNativeApp } from './utils/installerSms';
import {
    hasPageAccess,
    loadMyPageAccess,
    pathToPageKey
} from './utils/pageAccess';

setupApiClient();
setupAxiosAuth();

const SessionGuard = ({ children }) => {
    const [ready, setReady] = useState(
        !isInstallerNativeApp()
    );

    useEffect(() => {
        let stop = () => {};
        let cancelled = false;

        const boot = async () => {
            if (isInstallerNativeApp()) {
                await hydrateInstallerSession();
            }
            if (cancelled) return;
            stop = startSessionWatchers();
            setReady(true);
        };

        boot();

        return () => {
            cancelled = true;
            stop();
        };
    }, []);

    useEffect(() => {
        if (!ready) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        loadMyPageAccess().catch(() => {});
    }, [ready]);

    if (!ready) {
        return null;
    }

    return children;
};

const portalRedirect = userRole =>
    resolvePortalPath(userRole);

const ProtectedRoute = ({
    children,
    allowedRoles,
    pageKey
}) => {
    const userString = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    const location = useLocation();

    if (!userString || !token) {
        return (
            <Navigate
                to="/login"
                state={{ from: location }}
                replace
            />
        );
    }

    const lastActivity = getLastActivityAt();
    const idleLimit = isInstallerSession()
        ? 30 * 24 * 60 * 60 * 1000
        : IDLE_TIMEOUT_MS;

    if (
        lastActivity &&
        Date.now() - lastActivity >= idleLimit
    ) {
        clearSessionStorage();
        return <Navigate to="/login?reason=idle" replace />;
    }

    let user;
    try {
        user = JSON.parse(userString);
    } catch (e) {
        clearSessionStorage();
        return <Navigate to="/login" replace />;
    }

    const userRole = parseInt(user.role_id, 10);

    if (isNaN(userRole)) {
        clearSessionStorage();
        return <Navigate to="/login" replace />;
    }

    /*
     * Порталы заказчика / монтажника — жёстко,
     * не через матрицу страниц.
     */
    if (userRole === 4) {
        return location.pathname === '/customer-cabinet'
            ? children
            : (
                <Navigate
                    to="/customer-cabinet"
                    replace
                />
              );
    }

    if (userRole === 5) {
        return location.pathname === '/installer' ||
            location.pathname.startsWith('/installer/')
            ? children
            : <Navigate to="/installer/work" replace />;
    }

    /* Админ — полный доступ к CRM (в т.ч. пункт «Права»). */
    if (userRole === 1) {
        return children;
    }

    const resolvedKey =
        pageKey || pathToPageKey(location.pathname);

    if (resolvedKey) {
        const allowed = hasPageAccess(resolvedKey, 'view');
        if (allowed === true) {
            return children;
        }
        if (allowed === false) {
            return (
                <Navigate
                    to={portalRedirect(userRole)}
                    replace
                />
            );
        }
    }

    if (allowedRoles && !allowedRoles.includes(userRole)) {
        return (
            <Navigate
                to={portalRedirect(userRole)}
                replace
            />
        );
    }

    return children;
};

const App = () => {
    const staffRoles = [1, 2, 3];
    const installerRoles = [5];
    const hrRoles = [1, 6, 8];
    const dashboardRoles = [1, 2, 3, 6, 7, 8];
    const allRoles = [1, 2, 3, 4, 5, 6, 7, 8];
    const [isNative] = useState(() =>
        isInstallerNativeApp()
    );
    const Router = isNative ? HashRouter : BrowserRouter;

    return (
        <Router>
            <InstallerAppUpdateGate />
            <SessionGuard>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                        path="/oauth/callback"
                        element={<AvitoOAuthCallbackPage />}
                    />

                    <Route path="/dashboard" element={<ProtectedRoute pageKey="dashboard" allowedRoles={dashboardRoles}><Dashboard /></ProtectedRoute>} />
                    <Route path="/financial-articles" element={<ProtectedRoute pageKey="financial-articles" allowedRoles={staffRoles}><FinancialArticlesPage /></ProtectedRoute>} />
                    <Route path="/price-types" element={<ProtectedRoute pageKey="price-types" allowedRoles={staffRoles}><PriceTypesPage /></ProtectedRoute>} />
                    <Route path="/counterparties" element={<ProtectedRoute pageKey="counterparties" allowedRoles={staffRoles}><CounterpartiesPage /></ProtectedRoute>} />
                    <Route path="/nomenclature" element={<ProtectedRoute pageKey="nomenclature" allowedRoles={staffRoles}><NomenclaturePage /></ProtectedRoute>} />
                    <Route path="/tasks" element={<ProtectedRoute pageKey="tasks" allowedRoles={staffRoles}><TaskPage /></ProtectedRoute>} />
                    <Route path="/bitrix-tasks" element={<ProtectedRoute pageKey="bitrix-tasks" allowedRoles={[1, 2, 3, 6, 7, 8]}><BitrixTasksPage /></ProtectedRoute>} />
                    <Route path="/bitrix-tasks/:id" element={<ProtectedRoute pageKey="bitrix-tasks" allowedRoles={[1, 2, 3, 6, 7, 8]}><BitrixTaskDetailPage /></ProtectedRoute>} />
                    <Route path="/calendar" element={<ProtectedRoute pageKey="calendar" allowedRoles={[1, 2, 3, 6, 7, 8]}><CalendarPage /></ProtectedRoute>} />
                    <Route path="/tasksNew" element={<ProtectedRoute pageKey="tasks" allowedRoles={staffRoles}><TaskNewPage /></ProtectedRoute>} />
                    <Route path="/task-manager" element={<ProtectedRoute pageKey="task-manager" allowedRoles={staffRoles}><TaskManagerPage /></ProtectedRoute>} />
                    <Route path="/task-manager/settings" element={<ProtectedRoute pageKey="task-manager-settings" allowedRoles={staffRoles}><TaskManagerSettingsPage /></ProtectedRoute>} />
                    <Route path="/task-manager/fields" element={<ProtectedRoute pageKey="task-manager-fields" allowedRoles={staffRoles}><TaskManagerFieldsPage /></ProtectedRoute>} />
                    <Route path="/task-manager/dictionaries" element={<ProtectedRoute pageKey="task-manager-dictionaries" allowedRoles={staffRoles}><TaskManagerDictionariesPage /></ProtectedRoute>} />
                    <Route path="/task-manager/field-permissions" element={<ProtectedRoute pageKey="task-manager-field-permissions" allowedRoles={[1]}><TaskManagerFieldPermissionsPage /></ProtectedRoute>} />
                    <Route path="/task-manager/access" element={<ProtectedRoute pageKey="task-manager-access" allowedRoles={staffRoles}><TaskManagerAccessConstructorPage /></ProtectedRoute>} />
                    <Route path="/task-manager/funnels" element={<Navigate to="/task-manager/stage-routes" replace />} />
                    <Route path="/task-manager/stage-routes" element={<ProtectedRoute pageKey="task-manager-stage-routes" allowedRoles={staffRoles}><TaskManagerFunnelsPage /></ProtectedRoute>} />
                    <Route path="/task-manager/projects" element={<ProtectedRoute pageKey="task-manager-projects" allowedRoles={staffRoles}><TaskManagerProjectsPage /></ProtectedRoute>} />
                    <Route path="/task-manager/organizations" element={<ProtectedRoute pageKey="task-manager-organizations" allowedRoles={staffRoles}><TaskManagerOrganizationsPage /></ProtectedRoute>} />
                    <Route path="/task-manager/sites" element={<ProtectedRoute pageKey="task-manager-sites" allowedRoles={staffRoles}><TaskManagerSitesPage /></ProtectedRoute>} />
                    <Route path="/task-manager/work-type-stages" element={<ProtectedRoute pageKey="task-manager-work-type-stages" allowedRoles={staffRoles}><TaskManagerWorkTypeStagesPage /></ProtectedRoute>} />
                    <Route path="/task-manager/statuses" element={<ProtectedRoute pageKey="task-manager-statuses" allowedRoles={staffRoles}><TaskManagerStatusesPage /></ProtectedRoute>} />
                    <Route path="/constructor" element={<ProtectedRoute pageKey="constructor" allowedRoles={staffRoles}><ScenariosPage /></ProtectedRoute>} />
                    <Route path="/templates-incomerce" element={<ProtectedRoute pageKey="templates-incomerce" allowedRoles={staffRoles}><IncomercePage /></ProtectedRoute>} />
                    <Route path="/invoices" element={<ProtectedRoute pageKey="invoices" allowedRoles={staffRoles}><InvoicesPage /></ProtectedRoute>} />
                    <Route path="/users" element={<ProtectedRoute pageKey="users" allowedRoles={[1, 7]}><UsersPage /></ProtectedRoute>} />
                    <Route path="/org-structure" element={<ProtectedRoute pageKey="org-structure" allowedRoles={[1, 7]}><OrgStructurePage /></ProtectedRoute>} />
                    <Route path="/user-activity" element={<ProtectedRoute pageKey="user-activity" allowedRoles={[1, 7]}><UserActivityLogsPage /></ProtectedRoute>} />
                    <Route path="/priveleg" element={<ProtectedRoute pageKey="priveleg" allowedRoles={[1]}><PrivilegesPage /></ProtectedRoute>} />
                    <Route path="/max-messenger" element={<ProtectedRoute pageKey="max-messenger" allowedRoles={[1]}><MaxMessengerPage /></ProtectedRoute>} />
                    <Route path="/keyword-watch" element={<ProtectedRoute pageKey="keyword-watch" allowedRoles={[1]}><KeywordWatchPage /></ProtectedRoute>} />
                    <Route path="/goskey" element={<ProtectedRoute pageKey="goskey" allowedRoles={[1]}><GoskeySignPage /></ProtectedRoute>} />
                    <Route path="/kedo" element={<KedoLandingPage />} />
                    <Route path="/kedo/login" element={<KedoLoginPage />} />
                    <Route path="/kedo/register" element={<KedoRegisterPage />} />
                    <Route
                        path="/kedo/app"
                        element={
                            <KedoAuthGuard>
                                <KedoLayout />
                            </KedoAuthGuard>
                        }
                    >
                        <Route index element={<KedoDashboardPage />} />
                        <Route path="documents" element={<KedoDocumentsPage />} />
                        <Route path="documents/:id" element={<KedoDocumentPage />} />
                        <Route path="settings" element={<KedoSettingsPage />} />
                        <Route path="counterparties" element={<KedoCounterpartiesPage />} />
                        <Route path="mchd" element={<KedoMchdPage />} />
                        <Route path="audit" element={<KedoAuditPage />} />
                    </Route>
                    <Route path="/tmc" element={<ProtectedRoute pageKey="tmc" allowedRoles={[1, 2, 3]}><TmcWarehousePage /></ProtectedRoute>} />
                    <Route path="/customer-order-compare" element={<ProtectedRoute pageKey="customer-order-compare" allowedRoles={[1, 2]}><CustomerOrderComparePage /></ProtectedRoute>} />
                    <Route path="/bp-closing" element={<ProtectedRoute pageKey="bp-closing" allowedRoles={[1, 2]}><BpClosingPage /></ProtectedRoute>} />
                    <Route path="/daily-reports" element={<ProtectedRoute pageKey="daily-reports" allowedRoles={[1, 2, 3]}><DailyReportsPage /></ProtectedRoute>} />
                    <Route path="/docs" element={<ProtectedRoute pageKey="docs" allowedRoles={[1, 2, 3]}><DocumentCreatePage /></ProtectedRoute>} />
                    <Route path="/subcontracts" element={<ProtectedRoute pageKey="subcontracts" allowedRoles={[1, 2, 3]}><SubcontractsPage /></ProtectedRoute>} />
                    <Route path="/doc-workflows" element={<ProtectedRoute pageKey="doc-workflows" allowedRoles={staffRoles}><DocWorkflowKanbanPage /></ProtectedRoute>} />
                    <Route path="/invoice-approval" element={<ProtectedRoute pageKey="invoice-approval" allowedRoles={staffRoles}><InvoiceApprovalPage /></ProtectedRoute>} />
                    <Route path="/chat" element={<ProtectedRoute pageKey="chat" allowedRoles={[1, 2, 3, 6, 7, 8]}><EmployeeChatPage /></ProtectedRoute>} />
                    <Route path="/edit-stat" element={<ProtectedRoute pageKey="edit-stat" allowedRoles={staffRoles}><VariablesInfoPage /></ProtectedRoute>} />
                    <Route path="/transcription" element={<ProtectedRoute pageKey="transcription" allowedRoles={[1, 2, 3, 6, 7, 8]}><TranscriptionPage /></ProtectedRoute>} />
                    <Route path="/route-calculator" element={<ProtectedRoute pageKey="route-calculator" allowedRoles={staffRoles}><RouteCalculatorPage /></ProtectedRoute>} />
                    <Route path="/my-tasks" element={<ProtectedRoute allowedRoles={allRoles}><UserTasksPage /></ProtectedRoute>} />
                    <Route path="/registries" element={<ProtectedRoute pageKey="registries" allowedRoles={staffRoles}><RegistryCreatePage /></ProtectedRoute>} />
                    <Route path="/installer" element={
                        <ProtectedRoute allowedRoles={installerRoles}>
                            <InstallerTimesheetPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/installer/work" element={
                        <ProtectedRoute allowedRoles={installerRoles}>
                            <InstallerWorkPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/installer/tmc" element={
                        <ProtectedRoute allowedRoles={installerRoles}>
                            <InstallerTmcPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/" element={<ProtectedRoute allowedRoles={allRoles}><Navigate to="/dashboard" replace /></ProtectedRoute>} />

                    <Route
                        path="/bitrix-analytics"
                        element={
                            <ProtectedRoute pageKey="bitrix-analytics" allowedRoles={hrRoles}>
                                <BitrixAnalyticsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/hr/deals"
                        element={
                            <ProtectedRoute pageKey="hr-deals" allowedRoles={hrRoles}>
                                <HrDealsKanbanPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/hr/staff-registry"
                        element={
                            <ProtectedRoute
                                pageKey="hr-staff-registry"
                                allowedRoles={hrRoles}
                            >
                                <HrStaffRegistryPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/customer-cabinet"
                        element={
                            <ProtectedRoute allowedRoles={[4]}>
                                <CustomerCabinetPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <EmployeeChatWidget />
            </SessionGuard>
        </Router>
    );
};

export default App;
